import {ACTIONABLES, IActionable, IActionableList, IS_ActionableEnabled, OActionableListInit} from "/@back@/commons/actionables.js";
import {BASIS, OSkinableInit, PickInit} from "/@back@/commons/basis.js";
import {ActionBtn, Button} from "/@back@/commons/widgets/buttons.js";
import {ACTION, Action, EButtonUiContext, IAction} from "/@lib@/commons/actions.js";
import {REG} from "/@lib@/commons/registry.js";
import {isRefreshHook} from "/@lib@/commons/utils/signboard.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {
	DEBUG_TOOLTIP,
	EPopupType,
	findPopupableParent as findPopupableParentImp,
	IPopupable,
	IPopupCloseEvent,
	IS_Popupable,
	OAroundPosition,
	OFixedPosition,
	OInitSize,
	OPopupAnchor,
	OResizeObserverInit,
	OResizerPointer,
	OTitleBarPointer,
	OViewPortPosition,
	Popupable
} from "/@back@/commons/widgets/popupable.js";
import {IView} from "/@lib@/commons/views.js";
import {GridSmall} from "/@back@/commons/widgets/grid-tags.js";
import {GridColDef, ICellBuilder} from "/@back@/commons/widgets/grid-core.js";
import {GridDataHolderJsonArray} from "/@back@/commons/widgets/grid-libs.js";


/**
 * Popup de type menu, générique avec contenu libre.
 */
export interface IPopupMenu {
	initialize(init?: OPopupMenuInit): this;
}

export interface OPopupMenuInit extends OTitleBarPointer, OResizeObserverInit, OResizerPointer, OSkinableInit {
	restoreFocus?: HTMLElement;
}

export interface PopupMenu {
	initialize(init?: OPopupMenuInit): this;
}

export class PopupMenu extends Popupable {

	static readonly popupType = EPopupType.menu;

	/**
	 * A la fermeture du popup focus cet élement
	 * (réalisé avant l'execution de l'action qui peut réaffecter librement le focus).
	 */
	restoreFocus?: HTMLElement;

	protected _initialize(init: OPopupMenuInit) {
		super._initialize(init);
		if ('restoreFocus' in init) this.restoreFocus = init.restoreFocus;
	}

	/** Surcharge de MxPopupable.show()*/
	show(anchor?: OPopupAnchor, uiContainer?: Node, logicalParent?: Element): boolean {
		this._restoreFocus = this.restoreFocus;
		return super.show(anchor, uiContainer, logicalParent);
	}
}

REG.reg.registerSkin('c-popup-menu', 1, /* language=CSS */ `
	:host([opened]) {
		pointer-events: auto;
	}

	::slotted([role=menu][slot=subPopup]) {
		pointer-events: none;
	}

	#content {
		background-color: var(--row-bgcolor);
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
	}
`);

customElements.define('c-popup-menu', PopupMenu);

/**
 * c-popup : popup générique
 * c-popup-actions : popup affichant un menu d'actions.
 */


/**
 * Popup de type menu affichant une liste d'actions.
 * Classes:
 *  - hideIcons masque la colonne des icones pour toutes les actions de ce menu.
 */
export interface PopupActions<C> {
	initialize(init?: OPopupActionsInit<C>): this;
}

export interface OPopupActionsInit<C> extends OPopupMenuInit, OActionableListInit<C>, PickInit<PopupActions<C>, 'headTitle' | 'headBuilder'> {
}

export class PopupActions<C> extends Popupable implements IActionableList<C> {

	static readonly popupType = EPopupType.menu;

	uiContext: EButtonUiContext;

	groupOrder: string;

	protected _actions: IAction<C>[];
	protected _actionContext: C;

	get actions(): IAction<C>[] {
		return this._actions
	}

	set actions(actions: IAction<C>[]) {
		if (this._actions !== actions) {
			this._actions = this.groupOrder == null ? actions : ACTION.injectSepByGroup(actions, this.groupOrder, this._actionContext);
			this.refresh();
		}
	}

	get actionContext(): C {
		return this._actionContext
	}

	/** Titre commun aux actions. */
	get headTitle(): string {
		return this.getAttribute("head-title")
	}

	headBuilder?: (root: HTMLElement) => void;

	protected _initialize(init: OPopupActionsInit<C>) {
		super._initialize(init);
		REG.findReg(this).installSkin("scroll/small", this.shadowRoot);
		this._actionContext = init.actionContext;
		this.uiContext = init.uiContext || 'bar';
		this.groupOrder = init.groupOrder;
		this.actions = init.actions;

		if ('headTitle' in init) this.setAttribute("head-title", init.headTitle);
		if ('restoreFocus' in init) this.restoreFocus = init.restoreFocus;
		if ('headBuilder' in init) this.headBuilder = init.headBuilder;
		this.addEventListener('c-actioned', function (this: PopupActions<C>, ev: CustomEvent) {
			const actionable = ev.detail.actionable as IActionable<C>;
			if (!actionable.action.isMenu(actionable.actionContext)) {
				this.close(actionable.action);
			}
		});
		this.addEventListener('pointerover', function (this: PopupActions<C>, ev: MouseEvent) {
			const target = ev.target as HTMLElement;
			if (IS_ActionableEnabled(target) && target.parentNode == this) {
				target.focus();
				for (const subPopup of this._subPopupSlot.assignedNodes() as IPopupable[]) {
					subPopup.close();
				}
				// Ouverture du sous-menu au survol de l'item après un timeoutet au moins un mouvement de souris
				window.addEventListener('pointermove', () => {
					if (target.action.isMenu(target.actionContext)) {
						const openTimeout = setTimeout(() => target.click(), 300);
						this.addEventListener('pointerout', () => clearTimeout(openTimeout), {once: true});
					}
				}, {once: true})
			}
		});
	}


	buildInitFromAtts(init?: OPopupActionsInit<C>): OPopupActionsInit<C> {
		init = super.buildInitFromAtts(init);
		init.actions = ACTIONABLES.extractActionsFromDom(this, init);
		init.actionContext = ACTIONABLES.extractActionContextFromDom(this, init);
		if (this.hasAttribute('ui-context')) init.uiContext = BASIS.extractAttr(this, "ui-context");
		if (this.hasAttribute('group-order')) init.groupOrder = BASIS.extractAttr(this, "group-order");
		return init;
	}

	onKeyDown(ev: KeyboardEvent): boolean {
		//console.log("onKeyDown:::", ev.key, ev.target, ev);
		if (ACTION.isAnyControlPressed(ev)) return false;
		let next: HTMLElement;
		switch (ev.key) {
		case 'ArrowDown':
			if (ev.target === this) {
				next = DOM.findFirstChild(ev.target as Element, IS_ActionableEnabled);
			} else {
				next = DOM.findNextSibling(ev.target as Element, IS_ActionableEnabled);
			}
			break;
		case 'ArrowUp':
			if (ev.target === this) {
				next = DOM.findLastChild(ev.target as Element, IS_ActionableEnabled);
			} else {
				next = DOM.findPreviousSibling(ev.target as Element, IS_ActionableEnabled);
			}
			break;
		case 'ArrowLeft':
			if (this.parentPopup) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				// (ev.target as Element).setAttribute("xxxxx", Math.random().toString());
				// console.log("ArrowLeft----CLOSE::::", ev.composedPath(), ev.target);
				this.close();
				return true;
			}
			break;
		case 'ArrowRight':
			const actionBtn = ev.target;
			if (IS_ActionableEnabled(actionBtn) && actionBtn.action.isMenu(actionBtn.actionContext)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				actionBtn.click();
				return true;
			}
			break;
		}
		if (next) {
			next.focus();
			ev.preventDefault();
			ev.stopImmediatePropagation();
			return true;
		}
		return false;
	}

	/** Surcharge de Popupable.show()*/
	show(anchor?: OPopupAnchor, uiContainer?: Node, logicalParent?: Element): boolean {
		if (isRefreshHook(this.actionContext) && this.actionContext.onRefreshCycle(true) === 'stop') return;
		try {
			if (this._buildMenu() === 0) return false;
			return super.show(anchor, uiContainer, logicalParent);
		} finally {
			if (isRefreshHook(this.actionContext)) this.actionContext.onRefreshCycle(false);
		}
	}

	onViewHidden(): void {
		super.onViewHidden();
		this.textContent = null; //cleanup
	}

	protected _buildMenu(): number {
		//Construction du menu avec
		// - séparateur de groupes
		// - gestion de l'espacement homogène des colonnes si au moins un item est de type toggle.
		this.textContent = null;

		if (this.headBuilder) this.headBuilder(this.appendChild(<h5/>));
		else if (this.headTitle) this.appendChild(<h5>{this.headTitle}</h5>);

		let needCheckSpace = false;
		let count = 0;
		if (this.actions) for (let i = 0; i < this.actions.length; i++) {
			const action = this.actions[i];
			if (!action.isVisible(this.actionContext)) continue; //le menu étant reconstruit à chaque show, on peut évaluer la visibilité de suite.
			const menuItem = ActionBtn.buildButton(action, this.actionContext, 'menu', this);
			if (menuItem) {
				if (needCheckSpace) {
					if (!action.isToggle(this.actionContext)) menuItem.setAttribute('check-space', '');
				} else if (action.isToggle(this.actionContext)) {
					needCheckSpace = true;
					for (let prev = this.firstElementChild; prev; prev = prev.nextElementSibling) {
						prev.setAttribute('check-space', '');
					}
				}
				this.appendChild(menuItem);
				count++;
			}
		}
		ACTIONABLES.cleanupDoubleSep(this);
		return count;
	}

}

REG.reg.registerSkin('c-popup-actions', 1, /* language=CSS */ `
	:host([opened]) {
		pointer-events: auto;
	}

	::slotted([role=menu][slot=subPopup]) {
		pointer-events: none;
	}

	:host(.hideIcons) {
		--icon-size: 0;
	}

	#content {
		background-color: var(--row-bgcolor);
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
		overflow: auto;
	}

	::slotted(hr) {
		border: none;
		border-top: 1px solid var(--border-color);
		width: 100%;
		margin: .3em 0 !important;
		height: 1px; /* sinon border-top parfois non dessiné - chrome79 */
	}

	::slotted(h5) {
		margin: 0;
		padding: .3em .1em;
		background-color: var(--bgcolor);
		color: var(--color);
	}
`);

customElements.define('c-popup-actions', PopupActions);

/**
 * Popup de type dialogue modal.
 */
export interface OPopupDialogInit extends OSkinableInit, OTitleBarPointer, OResizeObserverInit, OResizerPointer, OViewPortPosition {

	/**
	 * En plus de la touche Escape, capte ctrl+Entrée pour clore le dialogue.
	 * A activer dans le contexte d'un dialogue avec changements d'états mais sans action explicite de validation (ie bouton Valider/Créer/Enregistrer).
	 * Usages : dialogue d'édition wed (metas), écran d'options ou préférences avec activation instantannée...
	 */
	closeOnCtrlEnter?: boolean;
}


export interface PopupDialog {
	initialize(init: OPopupDialogInit): this;
}

export class PopupDialog extends Popupable {
	static readonly popupType = EPopupType.dialog;

	closeOnCtrlEnter: boolean;

	protected _initialize(init: OPopupDialogInit) {
		super._initialize(init);
		this.closeOnCtrlEnter = init.closeOnCtrlEnter;
	}

	onKeyDown(ev: KeyboardEvent): boolean {
		if (this.closeOnCtrlEnter && ev.key === 'Enter' && ACTION.isAccelPressed(ev)) {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			this.tryClose();
			return true;
		}
		return false;
	}
}

REG.reg.registerSkin('c-popup-dialog', 1, /* language=CSS */ `
	:host([opened]) {
		pointer-events: auto;
		background-color: rgba(128, 128, 128, .4);
	}

	#content {
		border: 2px solid var(--dialog-bgcolor);
		border-radius: 5px;
		box-shadow: 1px 1px 4px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
		min-height: 6em;
		min-width: 12em;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	#body {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
		flex: 1;
	}
`);

customElements.define('c-popup-dialog', PopupDialog);


/**
 * Popup de type fenêtre flottante.
 */
export interface PopupFloating {
	initialize(init?: OPopupFloatingInit): this;
}

export interface OPopupFloatingInit extends OSkinableInit, OResizeObserverInit, OTitleBarPointer, OResizerPointer {
	/** Autoclose à la perte du focus. */
	closeOnBlur?: boolean
}

export class PopupFloating extends Popupable {
	static readonly popupType = EPopupType.floating;

	// TODO pour updater le titre/icon. this.titleBar = WindowBar instancié

	/** Si true à l'init, peut être passé à false pour stabiliser cette fenêtre indépendamment du focus. */
	closeOnBlur: boolean;

	protected _initialize(init: OPopupFloatingInit) {
		if (init.closeOnBlur) {
			this.closeOnBlur = true;
			this.addEventListener("focusout", this.onFocusout);
			this.tabIndex = 0;
		}
		super._initialize(init);
	}

	onFocusout(ev: FocusEvent) {
		//Si focus en dehors.
		if (!ev.relatedTarget || !(ev.relatedTarget instanceof Node) || !DOMSH.isFlatAncestor(this, ev.relatedTarget)) this.close();
	}

	onViewShown() {
		this.focus();
		super.onViewShown();
	}

}

REG.reg.registerSkin('c-popup-floating', 1, /* language=CSS */ `
	#body {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
	}

	#content {
		box-sizing: border-box;
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
	}
`);

customElements.define('c-popup-floating', PopupFloating);


export class PopupProgress extends PopupFloating {

	prg: HTMLProgressElement;

	get progressCallback(): (done: number, total: number | undefined) => void {
		return this.onProgress.bind(this)
	}

	onProgress(this: PopupProgress, done: number, total: number | undefined) {
		if (total > 0 && done < total) {
			this.prg.max = total;// * 1.5; //on considère que l'upload prend 2/3 du temps avant le traitement coté serveur.
			this.prg.value = done;
		} else {
			//taille upload inconnu OU upload achevé traitement en cours coté serveur.
			this.prg.max = 1;
			this.prg.removeAttribute("value");
		}
	}

	protected _initialize(init: OPopupFloatingInit) {
		super._initialize(init);
		this.prg = document.createElement("progress") as HTMLProgressElement;
		this.shadowRoot.getElementById("content").appendChild(this.prg);
	}
}

REG.reg.registerSkin('c-popup-progress', 1, /* language=CSS */ `
	#content {
		box-sizing: border-box;
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
	}

	progress {
		flex: 1;
		width: auto;
		margin: 0 .5em;
	}
`);

customElements.define('c-popup-progress', PopupProgress);

/**
 * Popup de notification.
 */
export interface PopupNotif {
	initialize(init?: OPopupNotifInit): this;
}

export interface OPopupNotifInit extends OSkinableInit {
	kind?: 'info' | 'warning' | 'error' | 'forbidden'
	level?: 'minor' | 'medium' | 'major'
	autoHide?: number
	noCloseBtn?: boolean
	/** classes pour stylage particulier :
	 * hover : notif au dessus d'une zone pour un petit message éphémère de recherche en cours.
	 */
	classes?: string
}

export class PopupNotif extends Popupable {
	static readonly popupType = EPopupType.floating;

	//icon?: 'info' | 'warning' | 'alert' | IEndPoint
	autoHide?: 0 | number;
	protected _autoHide?: 0 | number;

	get level(): 'minor' | 'medium' | 'major' {
		return this.getAttribute('level') as 'minor' | 'medium' | 'major'
	}

	get kind(): 'info' | 'warning' | 'error' | 'forbidden' {
		return this.getAttribute('kind') as any
	}

	set kind(k: 'info' | 'warning' | 'error' | 'forbidden') {
		DOM.setAttr(this, "kind", k);
	}

	protected _contentNode: Element;

	protected _initialize(init: OPopupNotifInit) {
		super._initialize(init);
		//this.icon = init.icon;
		if (init.level) this.setAttribute('level', init.level);
		if (init.kind) this.setAttribute('kind', init.kind);
		this.autoHide = init.autoHide == undefined ? null : init.autoHide;
		this.contentRoot.insertBefore(<span id="kind"/>, this.contentRoot.firstChild);
		if (init.classes) this.setAttribute("class", init.classes);
		if (!init.noCloseBtn) {
			// close btn hors tabIndex : pb si superposition de messages, stack à gérer...
			const btn = this.contentRoot.appendChild(<c-button title="Fermer cette notification" tabindex-custom="-1"/>) as Button;
			btn.onclick = function (ev: MouseEvent) {
				(DOMSH.findHost(ev.target as Node) as PopupNotif).tryClose()
			};
			btn.tabIndexCustom = -1;
		}
	}

	setContent(ct: string | Element): this {
		if (this._contentNode) this._contentNode.remove();
		if (typeof ct === 'string') {
			this._contentNode = this.appendChild(<div class="msg">{ct}</div>);
			this._autoHide = this.autoHide == null ? 2000 + ct.length * 20 : this.autoHide;
			if (this.opened) this.planifHide();
		} else {
			this._contentNode = this.appendChild(ct);
			this._autoHide = this.autoHide == null ? 2000 + ct.textContent.length * 20 : this.autoHide;
			if (this.opened) this.planifHide();
		}
		return this;
	}

	show(anchor?: OPopupAnchor, uiContainer?: Node, logicalParent?: Element): boolean {
		if (super.show(anchor || {viewPortX: 'middle', viewPortY: 'bottom', viewPortPadding: 10}, logicalParent)) {
			this.planifHide();
			return true;
		}
		return false;
	}

	close(): boolean {
		if (super.close()) {
			//reset de la largeur max qui a pu être imposé au positionnement de la notif.
			DOM.setStyle(this, 'max-width', null);
			return true;
		}
		return false;
	}

	protected planifHide() {
		if (this._timeout) clearTimeout(this._timeout);
		if (this._autoHide > 0) this._timeout = setTimeout(this.close.bind(this), this._autoHide);
	}

	protected _timeout: number;
}

REG.reg.registerSkin('c-popup-notif', 1, /* language=CSS */ `
	#content {
		flex-direction: row;
		align-items: center;
		border: solid 1px var(--border-color);
		border-radius: 4px;
		box-shadow: 1px 1px 5px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
		padding: 1em;
		margin-inline-end: .55em;
		z-index: 51;
	}

	:host(.hover) > #content {
		padding: .2em;
		opacity: .8;
		box-shadow: none;
	}

	:host([level='medium']) #content {
		z-index: 52;
	}

	:host([level='major']) #content {
		z-index: 53;
	}

	:host([kind='info']) #kind {
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
		background: no-repeat url(/@skin@/commons/widgets/popupable/info.svg);
	}

	:host([kind='warning']) #kind {
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
		background: no-repeat url(/@skin@/commons/widgets/popupable/warning.svg);
	}

	:host([kind='error']) #kind {
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
		background: no-repeat url(/@skin@/commons/widgets/popupable/error.svg);
	}

	:host([kind='forbidden']) #kind {
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
		background: no-repeat url(/@skin@/commons/widgets/popupable/forbidden.svg);
	}


	c-button {
		position: absolute;
		top: -.5rem;
		right: -.5rem;
		border: 1px solid var(--border-color);
		border-radius: .3rem;
		box-sizing: border-box;
		width: 1.3rem;
		height: 1.3rem;
		background: center / .8rem no-repeat url(/@skin@/commons/icons/close.svg) var(--bgcolor);
	}

	::slotted(.msg) {
		text-align: center;
		user-select: none;
		white-space: pre-wrap;
	}

	/* Pour c-popup-notif-actions. */
	slot[name=actions] > *:not([hidden]) {
		display: block;
		order: 1;
	}
`);

customElements.define('c-popup-notif', PopupNotif);

/**
 *
 */
export interface PopupNotifActions<C> {
	initialize(init?: OPopupNotifActionsInit<C>): this;
}

export interface OPopupNotifActionsInit<C> extends OPopupNotifInit {
	actions: IAction<C>[];
	actionContext: C;
}

export class PopupNotifActions<C> extends PopupNotif {
	_initialize(init: OPopupNotifActionsInit<C>) {
		init.skin = "c-popup-notif";
		super._initialize(init);
		const actionsBar = this.contentRoot.appendChild(<slot name="actions"/>);
		for (const action of init.actions) {
			const button = ActionBtn.buildButton(action, init.actionContext, 'dialog', actionsBar);
			actionsBar.appendChild(button);
		}
		return this;
	}
}

customElements.define('c-popup-notif-actions', PopupNotifActions);

/**
 * Popup de type tooltip.
 * Config attribute : hover-allowed
 */
export interface ITooltipPointer {
	popupTooltip?: PopupTooltip;
}

export function isTooltipPointer(n: any): n is ITooltipPointer {
	return n && (n as ITooltipPointer).popupTooltip != null
}

/**
 *
 */
export interface PopupTooltip {
	initialize(init?: OPopupTooltipInit): this;
}

export interface OPopupTooltipInit extends OSkinableInit, PickInit<PopupTooltip, 'tooltipOf' | 'anchor'> {
	/**
	 * 	true : le tooltip autorise la souris à passer dessus et permet ansi les interactions dans le tooltip.
	 * 'shift' : le tooltip n'autorise la souris à passer dessus QUE si la touche 'shift' est enfoncée.
	 */
	hoverAllowed?: boolean | 'shift'
}

export class PopupTooltip extends Popupable {
	static readonly popupType = EPopupType.tooltip;

	/** Elément auquel rattacher ce tooltip. Si null, c'est l'élément parent qui sera considéré. */
	tooltipOf: HTMLElement & ITooltipPointer;

	/** Positionnement du tooltip. Par défaut : en dessous de tooltipOf. */
	anchor?: OPopupAnchor;

	/** Autorise une interaction à la souris dans le tooltip (avec shift ou pas). */
	get hoverAllowed(): boolean {
		return this.hasAttribute('hover-allowed')
	};

	/** Autorise une interaction à la souris dans le tooltip si shift est enfoncé. */
	get hoverAllowedWithShift(): boolean {
		return this.getAttribute('hover-allowed') === "shift";
	};

	protected _lstn: (ev: MouseEvent) => void;

	protected _timer: number;
	protected _checkOwner: number | any /* bug typage TS2.9 incompréhensible... */;
	protected _checkOwnerFct: () => void;


	detachTooltip() {
		if (this.tooltipOf) {
			this.tooltipOf.removeEventListener('pointerenter', this._lstn);
			this.tooltipOf.removeEventListener('pointerleave', this._lstn);
			this.tooltipOf.removeEventListener('pointercancel', this._lstn);
			this.close();
			if (this.tooltipOf.popupTooltip === this) this.tooltipOf.popupTooltip = null; //helpGC
			this.tooltipOf = null; //helpGC
		}
	}

	protected _initialize(init: OPopupTooltipInit) {
		super._initialize(init);
		if (init.hoverAllowed) this.setAttribute('hover-allowed', init.hoverAllowed.toString());
		if (init.anchor) this.anchor = init.anchor;
		this.tooltipOf = (init.tooltipOf ? init.tooltipOf : DOMSH.getFlatParentElt(this)) as HTMLElement & ITooltipPointer;
		if (this.tooltipOf.popupTooltip) this.tooltipOf.popupTooltip.detachTooltip();
		this.tooltipOf.popupTooltip = this;
		this._lstn = (ev: MouseEvent) => {
			if (this._timer) {
				window.clearTimeout(this._timer);
				this._timer = 0;
			}
			if (ev.type === 'pointerenter') {
				this._timer = window.setTimeout(() => {
					this._timer = 0;
					if (this.tooltipOf && this.tooltipOf.isConnected) this.show(this.anchor || this.tooltipOf, this.tooltipOf);
				}, POPUP.TOOLTIP_retarder);
			} else {
				if (this.hoverAllowed && (!this.hoverAllowedWithShift || ev.shiftKey)) {
					if (ev.currentTarget === this.contentRoot) {
						const x = ev.clientX, y = ev.clientY;
						const r = this.tooltipOf.getBoundingClientRect();
						if (Math.floor(r.left) <= x + 1 && Math.ceil(r.right) >= x - 1 && Math.floor(r.top) <= y + 1 && Math.ceil(r.bottom) >= y - 1) return;
					}
					// Test de l'ensemble de la hiéarchie de tooltip sur le pointerleave
					let tooltip = this as IPopupable;
					do {
						if (DOMSH.isFlatAncestor(tooltip, ev.relatedTarget as Node)) break;
						const parent = tooltip.parentElement;
						tooltip.close();
						tooltip = IS_Popupable(parent) && parent.popupType === EPopupType.tooltip && parent;
					} while (tooltip);
				} else {
					this.close();
				}
			}
		};
		this.tooltipOf.addEventListener('pointerenter', this._lstn);
		if (!DEBUG_TOOLTIP) {
			this.tooltipOf.addEventListener('pointerleave', this._lstn);
			this.tooltipOf.addEventListener('pointercancel', this._lstn);
			if (this.hoverAllowed) {
				this.contentRoot.addEventListener('pointerleave', this._lstn);
				this.contentRoot.addEventListener('pointercancel', this._lstn);
			}
		}
	}

	show(anchor?: OPopupAnchor, uiContainer?: Node, logicalParent?: Element): boolean {
		const shown = super.show(anchor, uiContainer, logicalParent);
		if (shown && this.tooltipOf && !this._checkOwner) {
			//Controle que le owner du tooltip ne soit pas détaché (exemple double-click sur l'objet qui décharge le contexte courant et en ouvre un nouveau).
			if (!this._checkOwnerFct) {
				this._checkOwnerFct = () => {
					if (!this.tooltipOf.isConnected) this.close();
				};
			}
			this._checkOwner = setInterval(this._checkOwnerFct, 250);
		}
		return shown;
	}

	close(returnValue?: any): boolean {
		if (this._checkOwner) {
			clearInterval(this._checkOwner);
			this._checkOwner = 0;
		}
		return super.close(returnValue);
	}

	/** Pour la création à la volée du tooltip au 1er event pointerenter. */
	forwardPointerEnter(ev: MouseEvent) {
		this._lstn.call(this.tooltipOf, ev);
	}
}

REG.reg.registerSkin('c-popup-tooltip', 1, /* language=CSS */ `
	#content {
		display: block;
		border: solid 1px var(--tooltip-border-color, #fcffc8);
		border-radius: 3px;
		box-shadow: 1px 1px 2px var(--fade-color);
		background-color: var(--tooltip-bgcolor, #feffe5);
		z-index: 101;
		user-select: none;
	}
`);

customElements.define('c-popup-tooltip', PopupTooltip);


/**
 * Popup pour la gestion des entrées d'autocomplete, basé sur un grid, évitant
 * la prise de focus.
 *
 * Le type de données est quelconque : à la charge de l'appelant de fournir un
 * cellBuilder pour gérer l'affichage de la liste.
 *
 * La popup pouvant s'ouvrir et se fermer plusieurs fois, son résultat doit être
 * récupéré par l'évènement 'c-beforeclose'.
 */
export interface PopupAutoComplete<D> {
	initialize(init?: OPopupAutoCompleteInit<D>): this;
}

/**
 * Option d'initialisatiode la popup d'autocomplete
 */
export interface OPopupAutoCompleteInit<D> extends OSkinableInit {
	/** ICellBuilder pour la construction de la liste des entrées */
	cellBuilder: ICellBuilder;
	/** Entrées à affecter dès l'initialiation */
	entries?: D[];
}

/**
 * Option de création à la demande de la popup d'autocomplete
 */
export interface OPopupAutoCompleteCreate<D> extends OSkinableInit {
	/** Input sur lequel écouter les évènements */
	input: HTMLElement;

	/** Détermine si le premier élément doit-il être sélectionné automatiquement à
	 * l'affichage de la popup */
	autoSelOnShow?: boolean;

	/** Finder à appeler pour récupérer les entrées d'autocomplete */
	finder: IPopupAutoCompleteFinder<D>;
}

/** Interface entre la popup et le widget appelant */
export interface IPopupAutoCompleteFinder<D> {
	/**
	 * Méthode appelée pour récupérer les entrées, évalué à chaque event input.
	 * @return null si raceCond détectée (autre appel concurrent pendant la résolution de la Promise), [] si erreur/abandon appelant à ne pas afficher la popup.
	 */
	findAutoComplete: (newSession?: boolean, ev?: Event) => Promise<D[] | null>;

	/** Ancre sur laquelle positionner la popup, évaluée à chaque affichage.
	 * Par défaut, il s'agit de l'input */
	autoCompleteAnchor?: HTMLElement | ClientRect;
}

export class PopupAutoComplete<D> extends Popupable {
	static readonly popupType = EPopupType.tooltip;

	protected _grid: GridSmall;
	protected _data = new GridDataHolderJsonArray<D>();

	get entries(): D[] {
		return this._data.getDatas();
	}

	set entries(val) {
		this._data.setDatas(val);
	}

	protected _initialize(init: OPopupAutoCompleteInit<D>) {
		super._initialize(init);

		this._grid = this.contentRoot.appendChild(new GridSmall().initialize({
			selType: 'monoOver',
			columnDefs: [new GridColDef('main')
				.setFlex('1rem', 1, 1)
				.setCellBuilder(init.cellBuilder)
			],
			dataHolder: this._data,
			hideHeaders: true,
			skinOver: 'c-popup-autocomplete/grid',
			defaultAction: new Action<PopupAutoComplete<D>>().setExecute((self) => {
				const selection = self._data.getSelectedDatas();
				if (selection.length) this.close(selection[0]);
			}),
			defaultActionCtx: this,
			defaultActionOn: 'mousedown',
			skinScroll: 'scroll/small'
		}));

		this._grid.removeAttribute('tabindex');
		this.entries = init.entries || [];
	}

	/**
	 * Création de la popup à la demande dès qu'un caractère est entrée ou sur les actions double clic ou Alt+Enter.
	 *
	 * À appeler sur l'event focusin de l'input, les listeners étant enlevés automatiquement sur le focusout.
	 *
	 * @return Promesse de création de la popup
	 * */
	static createFromInputEvent<D>(createOpts: OPopupAutoCompleteCreate<D>, popupInit: OPopupAutoCompleteInit<D>): Promise<PopupAutoComplete<D>> {
		return new Promise((resolve) => {
			const {input} = createOpts;
			let popup: PopupAutoComplete<D>;

			function create(ev: Event) {
				if (popup) return;
				popup = new PopupAutoComplete<D>().initialize(popupInit);
				resolve(popup);
				find(true, ev);
				return popup;
			}

			function find(newSession: boolean, ev: Event) {
				createOpts.finder.findAutoComplete(newSession, ev).then((entries) => {
					if (entries == null) return;
					if (entries.length) {
						popup.entries = entries;
						popup.show({elem: createOpts.finder.autoCompleteAnchor || input}, input);
						if (createOpts.autoSelOnShow) popup._grid.setSelectedRows(0);
					} else {
						popup.close();
					}
				});
			}

			function onInput(ev: Event) {
				if (!create(ev)) find(false, ev);
			}

			function prevent(ev: Event) {
				ev.preventDefault();
				ev.stopPropagation();
			}

			function onKeyDown(ev: KeyboardEvent) {
				if (ev.key === "Enter" && ev.altKey) {
					prevent(ev);
					if (!create(ev)) find(false, ev);
				} else if (popup?.opened) {
					const {_grid: grid} = popup;
					let selected = grid.getSelectedRow();
					/* Déplacement de la sélection sans prise de focus */
					if (ev.key === "ArrowDown") {
						prevent(ev);
						if (selected === undefined) selected = -1;
						if (++selected < popup.entries.length) {
							grid.setSelectedRows(selected);
							grid.ensureRowVisible(selected);
						}
					} else if (ev.key === "ArrowUp") {
						prevent(ev);
						if (selected !== undefined) {
							if (--selected >= 0) {
								grid.setSelectedRows(selected);
								grid.ensureRowVisible(selected);
							} else grid.setSelectedRows(-1);
						}
					} else if (ev.key === "Enter") {
						if (selected !== undefined) ACTION.doOnEvent(grid.defaultAction.execute(grid.defaultActionCtx, ev), ev);
					} else if (ev.key === "Escape") {
						prevent(ev);
						popup.close();
					}
				}
			}

			input.addEventListener('input', onInput);
			document.addEventListener('selectionchange', onInput);
			input.addEventListener('dblclick', create, {once: true});
			input.addEventListener('keydown', onKeyDown);

			input.addEventListener('focusout', () => {
				input.removeEventListener('input', onInput);
				document.removeEventListener('selectionchange', onInput);
				input.removeEventListener('dblclick', create);
				input.removeEventListener('keydown', onKeyDown);
			}, {once: true})
		});
	}
}

REG.reg.registerSkin('c-popup-autocomplete', 1, /* language=CSS */ `
	#content {
		display: block;
		border: solid 1px var(--tooltip-border-color);
		border-radius: 3px;
		box-shadow: 1px 1px 2px var(--fade-color);
		z-index: 101;
		user-select: none;
		--row-bgcolor: var(--tooltip-bgcolor);
		--row-inSel-unfocus-bgcolor: var(--row-inSel-bgcolor);
	}
`);

REG.reg.registerSkin('c-popup-autocomplete/grid', 1, /* language=CSS */ `
	:host {
		border: none;
		max-height: 16em;
		max-width: 16em;
	}
`);

customElements.define('c-popup-autocomplete', PopupAutoComplete);

/**
 * Popup de confirmation (message et boutons Annuler,Ok)
 *
 * Les labels des boutons sont configurables.
 * Un slot 'details' est disponible pour enrichir le contenu de la boîte de dialogue.
 */

export interface PopupConfirm {
	initialize(init?: OPopupConfirmInit): this;
}

/**
 * Configuration d'une boîte de dialogue de confirmation.
 * Mettre null cancelLbl pour faire un simple dialogue d'alerte avec un seul bouton.
 */
export interface OPopupConfirmInit extends OSkinableInit, OTitleBarPointer, OResizeObserverInit, OResizerPointer {
	kind?: 'question' | 'info' | 'warning' | 'error' | 'forbidden' | null
	/** Label pour le bouton OK/Valider */
	okLbl?: string;
	/** Label pour le bouton d'annulation. Si null, le bouton n'est pas affiché (revient à un dialogue d'alerte) */
	cancelLbl?: string | null;
}

export class PopupConfirm extends Popupable<boolean> {
	static readonly popupType = EPopupType.dialog;

	get kind(): 'question' | 'info' | 'warning' | 'error' | 'forbidden' | null {
		return this.getAttribute('kind') as any
	}

	set kind(k: 'question' | 'info' | 'warning' | 'error' | 'forbidden' | null) {
		DOM.setAttr(this, "kind", k);
	}

	okButton: Button;
	cancelButton: Button | null;

	protected _contentNode: Element;

	protected _initialize(init: OPopupConfirmInit) {
		super._initialize(init);
		this.findReg(init).installSkin('webzone:panel', this.shadowRoot);
		if (init.kind) this.setAttribute('kind', init.kind);
		this.contentRoot.appendChild(<slot name="details"/>);
		const actionsBar = this.contentRoot.appendChild(<slot name="actions"/>);
		this.okButton = actionsBar.appendChild(<c-button ui-context="dialog" label={init.okLbl || (init.cancelLbl !== null ? "OK" : "Fermer")} class="default" onclick={this.onOK}/>) as Button;
		if (init.cancelLbl !== null) this.cancelButton = actionsBar.appendChild(<c-button ui-context="dialog" label={init.cancelLbl || "Annuler"} onclick={this.onCancel}/>) as Button;
		this.onkeypress = this.onKeyPress;
	}

	protected _buildBodySlot() {
		this._bodySlot = <slot id="body"/> as HTMLSlotElement;
		this.contentRoot.appendChild(<div id="msgBox">
			<span id="kind"/>
			{this._bodySlot}
		</div>);
	}


	/** Défini le contenu principal de la boîte de dialogue */
	setContent(ct: string | Element): this {
		if (this._contentNode) this._contentNode.remove();
		if (typeof ct === 'string') {
			this._contentNode = this.appendChild(<div class="msg">{ct}</div>);
		} else {
			this._contentNode = this.appendChild(ct);
		}
		return this;
	}

	onOK(this: Button) {
		if (!this.disabled)
			DOMSH.findHost<PopupConfirm>(this).tryClose(true);
	}

	onCancel(this: Button) {
		DOMSH.findHost<PopupConfirm>(this).tryClose(false);
	}

	onKeyPress(ev: KeyboardEvent) {
		if (ev.key === "Enter") this.tryClose(true);
		else if (ev.key === "Escape") this.tryClose(false);
	}
}

REG.reg.registerSkin('c-popup-confirm', 1, /* language=CSS */ `
	:host([opened]) {
		pointer-events: auto;
		background-color: rgba(0, 0, 0, .5);
		z-index: 1011;
	}

	#content {
		border: 2px solid var(--dialog-bgcolor);
		box-shadow: 1px 1px 4px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
		border-radius: 5px;
	}

	#msgBox {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	#body {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
		margin: .5em;
	}

	#kind {
		min-width: 1.5em;
		min-height: 1.5em;
		margin: .5em;
		filter: var(--filter);
	}

	:host([kind='question']) #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/question.svg);
	}

	:host([kind='info']) #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/info.svg);
	}

	:host([kind='warning']) #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/warning.svg);
	}

	:host([kind='error']) #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/error.svg);
	}

	:host([kind='forbidden']) #kind {
		background: center no-repeat url(/@skin@/commons/widgets/popupable/forbidden.svg);
	}

	::slotted([slot=details]) {
		display: block;
		margin: .5em 2em;
	}

	slot[name=actions] {
		display: flex;
		min-height: min-content;
		min-width: 0;
		flex-direction: row;
		justify-content: flex-end;
	}

	slot[name=actions] > c-button {
		margin: 1em .5em;
	}
`);

customElements.define('c-popup-confirm', PopupConfirm);

/**
 * Configuration d'une boîte de dialogue d'invite
 */
export interface OPopupPromptInit extends OSkinableInit, OTitleBarPointer, OResizeObserverInit, OResizerPointer {
	/** Label pour le bouton OK/Valider */
	okLbl?: string;
	/** Label pour le bouton d'annulation. Si null, le bouton n'est pas affiché (revient à un dialogue d'alerte) */
	cancelLbl?: string | null;
	/** La valeur de l'input est-elle requise */
	inputRequired?: boolean;
}

export class PopupPrompt extends Popupable {
	static readonly popupType = EPopupType.dialog;

	okButton: Button;
	cancelButton: Button;

	protected _contentNode: Element;

	protected _inputRequired: boolean;

	protected _initialize(init: OPopupPromptInit) {
		super._initialize(init);
		this._inputRequired = init.inputRequired;
		this.contentRoot.appendChild(<slot name="details"/>);
		const actionsBar = this.contentRoot.appendChild(<slot name="actions"/>);
		this.okButton = actionsBar.appendChild(<c-button ui-context="dialog" label={init.okLbl || "OK"} class="default" onclick={this.onOK}/>) as Button;
		this.cancelButton = actionsBar.appendChild(<c-button ui-context="dialog" label={init.cancelLbl || "Annuler"} onclick={this.onCancel}/>) as Button;
		this.onkeypress = this.onKeyPress;
	}

	/** Défini le contenu principal de la boîte de dialogue */
	setContent(ct: string | Element): this {
		if (this._contentNode) this._contentNode.remove();
		if (typeof ct === 'string') {
			this._contentNode = this.appendChild(<div class="content">
					<div class="msg">{ct}</div>
					<input required={this._inputRequired ? "" : undefined}/>
				</div>
			);
		} else {
			this._contentNode = this.appendChild(ct);
		}
		return this;
	}

	onOK(this: Button) {
		const self = DOMSH.findHost<PopupPrompt>(this);
		const input = self.querySelector("input");
		if (input.reportValidity()) {
			self.tryClose(input.value);
		}
	}

	onCancel(this: Button) {
		DOMSH.findHost<PopupPrompt>(this).tryClose();
	}

	onKeyPress(ev: KeyboardEvent) {
		if (ev.key === "Enter") this.onOK.call(this.okButton)
		else if (ev.key === "Escape") this.tryClose();
	}
}

REG.reg.registerSkin('c-popup-prompt', 1, /* language=CSS */ `
	:host([opened]) {
		pointer-events: auto;
		background-color: rgba(0, 0, 0, .5);
	}

	#content {
		border: 2px solid var(--dialog-bgcolor);
		box-shadow: 1px 1px 4px var(--fade-color);
		background-color: var(--bgcolor);
		color: var(--color);
		border-radius: 5px;
		min-width: 5em;
		min-height: 2em;
	}

	#body {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		overflow: auto;
		margin: .5em;
	}

	::slotted(.content) {
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 5em;
		justify-content: space-evenly;
	}

	::slotted(.content) > * {
		margin: 0.5em 0;
	}

	::slotted([slot=details]) {
		display: block;
		margin: .5em 2em;
	}

	slot[name=actions] {
		display: flex;
		min-height: min-content;
		min-width: 0;
		flex-direction: row;
		justify-content: flex-end;
	}

	slot[name=actions] > c-button {
		margin: 1em .5em;
	}
`);

customElements.define('c-popup-prompt', PopupPrompt);

export namespace POPUP {

	/**
	 * Affiche une boîte de dialogue de confirmation ou d'alerte si le bouton d'annulation est masqué
	 * via les options.
	 *
	 * @param msg message ou noeud à afficher en tant que contenu de la boîte de dialogue
	 * @param options Voir {@link OPopupConfirmInit}
	 * @return Une promesse renvoyant true si validé, false si annulé.
	 */
	export async function confirm(msg: string | Element, from: Node, options?: OPopupConfirmInit): Promise<boolean> {
		if (!options) options = {};
		if (!('kind' in options)) options.kind = options.cancelLbl !== null ? 'question' : 'info';
		const popup = new PopupConfirm().initialize(options);
		popup.setContent(msg);

		popup.show({
			viewPortX: 'middle',
			viewPortY: '1/3',
		}, from);
		return popup.onNextClose();
	}

	/**
	 * Affiche une boîte de dialogue de confirmation ou d'alerte avec input de choix (ex: 'Ne plus afficher ce message').
	 *
	 *
	 * @param msg message ou noeud à afficher en tant que contenu de la boîte de dialogue
	 * @param checkLbl Label de l'input de choix
	 * @param checkSt État initial de l'input de choix
	 * @param options Voir {@link OPopupConfirmInit}
	 * @return Une promesse renvoyant si le statut du checkbox (checked|unchecked) si le dialogue ou validé,
	 *      false si annulé. La promesse n'est jamais rejetée.
	 */
	export async function confirmCheck(msg: string | Element, from: Node, checkLbl: string, checkSt?: boolean, options?: OPopupConfirmInit): Promise<false | 'checked' | 'unchecked'> {
		if (!options) options = {};
		if (!('kind' in options)) options.kind = options.cancelLbl !== null ? 'question' : 'info';
		const popup = new PopupConfirm().initialize(options);
		popup.setContent(msg);

		const input = <input type="checkbox" checked={checkSt}/> as HTMLInputElement;
		input.checked = checkSt;
		popup.appendChild(<label slot="details">
			{input}
			<span>{checkLbl}</span>
		</label>);

		popup.show({
			viewPortX: 'middle',
			viewPortY: '1/3',
		}, from);

		const result = await popup.onNextClose();
		if (result) return input.checked ? 'checked' : 'unchecked';
		else return false;
	}

	/** Affiche un menu-contextuel. */
	export function showPopupActions<C>(init: OPopupActionsInit<C>, anchor: OPopupAnchor, from: Node): Promise<Action<C>> {
		if (!init.actions || init.actions.length === 0) return Promise.resolve(null);
		const popupMenu = new PopupActions<C>().initialize(init);
		popupMenu.show(anchor, from);
		return new Promise((resolve) => {
			popupMenu.addEventListener('c-close', (ev: IPopupCloseEvent) => {
				resolve(ev.detail.returnValue);
			})
		});
	}

	/**
	 * Affiche une boîte de dialogue de confirmation ou d'alerte si le bouton d'annulation est masqué
	 * via les options.
	 *
	 * @param msg message ou noeud à afficher en tant que contenu de la boîte de dialogue
	 * @param options Voir {@link OPopupConfirmInit}
	 * @return Une promesse renvoyant true si validé, false si annulé.
	 */
	export async function prompt(msg: string | Element, from: Node, options?: OPopupPromptInit): Promise<string> {
		const popup = new PopupPrompt().initialize(options);
		popup.setContent(msg);

		popup.show({
			viewPortX: 'middle',
			viewPortY: '1/3',
		}, from);
		return popup.onNextClose();
	}

	/**
	 * Affiche un menu-contextuel positionné à partir de la position
	 * de la souris d'un event ou, à défaut, de la position du target de l'event.
	 *
	 * @param fromContext Nécessaire si ev.target est issu d'un menu déconnecté pour retrouver la stack des dialogues courants.
	 */
	export function showPopupActionsFromEvent<C>(init: OPopupActionsInit<C>, ev: Event | HTMLElement, fromContext?: HTMLElement): Promise<Action<C>> {
		if (ev instanceof MouseEvent && ev.pageX > 0 && ev.pageY > 0) {
			return showPopupActions(init, {initX: ev.pageX, initY: ev.pageY}, fromContext || ev.target as Node);
		} else if (ev instanceof Event) {
			return showPopupActions(init, fromContext || ev.target as HTMLElement, fromContext || ev.target as Node);
		} else {
			return showPopupActions(init, ev, fromContext || ev);
		}
	}

	/** Affiche une fenêtre de dialogue modale. */
	export function showDialog<R = {}>(content: Node, from: Node, init?: OPopupDialogInit): IPopupable<R> {
		if (!init) init = {};
		const dialog = new PopupDialog().initialize(init);
		dialog.usedOnce = true;
		dialog.appendChild(content);
		if (!init.viewPortY) init.viewPortY = '1/3';
		if (!('fixSize' in init)) init.fixSize = true;
		dialog.show(init, from);
		return dialog;
	}

	/** Affiche une notification, non modale. */
	export function showNotif(content: string | Element, from: Node, init?: OPopupNotifInit | OPopupNotifActionsInit<any>, anchor?: OPopupAnchor): PopupNotif {
		if (!init) init = {};
		const notif = 'actions' in init ? new PopupNotifActions() : new PopupNotif();
		if ('actions' in init && !('autoHide' in init)) init.autoHide = 0; //pas de timer d'effacement si actions.
		notif.initialize(init);
		notif.usedOnce = true;
		notif.setContent(content);
		notif.show(anchor, from);
		return notif;
	}

	/** Affiche une notification, non modale avec une icone de type "info". */
	export function showNotifInfo(content: string | Element, from: Node, init?: OPopupNotifInit | OPopupNotifActionsInit<any>, anchor?: OPopupAnchor): PopupNotif {
		if (init) init.kind = "info";
		else init = {kind: "info"};
		return showNotif(content, from, init, anchor);
	}

	/** Affiche une notification, non modale avec une icone de type "alerte". */
	export function showNotifWarning(content: string | Element, from: Node, init?: OPopupNotifInit | OPopupNotifActionsInit<any>, anchor?: OPopupAnchor): PopupNotif {
		if (init) init.kind = "warning";
		else init = {kind: "warning"};
		return showNotif(content, from, init, anchor);
	}

	/** Affiche une notification, non modale avec une icone de type "erreur". */
	export function showNotifError(content: string | Element, from: Node, init?: OPopupNotifInit | OPopupNotifActionsInit<any>, anchor?: OPopupAnchor): PopupNotif {
		if (init) init.kind = "error";
		else init = {kind: "error"};
		return showNotif(content, from, init, anchor);
	}

	/** Affiche une notification, non modale avec une icone de type "interdit". */
	export function showNotifForbidden(content: string | Element, from: Node, init?: OPopupNotifInit | OPopupNotifActionsInit<any>, anchor?: OPopupAnchor): PopupNotif {
		if (init) init.kind = "forbidden";
		else init = {kind: "forbidden"};
		return showNotif(content, from, init, anchor);
	}

	/** Affiche une fenêtre flottante, non modale. */
	export function showFloating<R = {}>(content: Node | ((popup: PopupFloating) => Node), anchor: OPopupAnchor, fromContext: Node, init?: OPopupFloatingInit): IPopupable<R> {
		const popup = new PopupFloating().initialize(init || {});
		popup.usedOnce = true;
		popup.appendChild(typeof content === "function" ? content(popup) : content);
		if (!('fixSize' in anchor) && !(anchor instanceof HTMLElement)) anchor.fixSize = true;
		popup.show(anchor, fromContext);
		return popup;
	}

	/**
	 * Affiche une fenêtre flottante de progression d'un upload et/ou download...
	 * Attention : non rattaché à son contexte applicatif (pas de logicalParent)
	 *
	 */
	export function showProgress(fromContext: Node, title: string, anchor?: OPopupAnchor): PopupProgress {
		const popup = new PopupProgress().initialize({titleBar: {barLabel: {label: title}}});
		popup.usedOnce = true;
		if (!anchor) anchor = {viewPortX: "end", viewPortY: "top", initWidth: "20em", initHeight: "4em"};
		if (!('fixSize' in anchor) && !(anchor instanceof HTMLElement)) anchor.fixSize = true;
		popup.show(anchor, fromContext, null);
		return popup;
	}


	/**
	 *
	 * @param fromContext Nécessaire si ev.target est issu d'un menu déconnecté pour retrouver la stack des dialogues courants.
	 */
	export function showFloatingFromEvent<R = {}>(content: Node, ev: Event | HTMLElement, fromContext?: HTMLElement, init?: OPopupFloatingInit, initSize?: OInitSize): IPopupable<R> {
		const anchor: OPopupAnchor = initSize ? initSize : {};
		if (ev instanceof MouseEvent && ev.pageX > 0 && ev.pageY > 0) {
			(anchor as OFixedPosition).initX = ev.pageX;
			(anchor as OFixedPosition).initY = ev.pageY;
			return showFloating(content, anchor, fromContext || ev.target as Node, init);
		} else if (ev instanceof Event) {
			(anchor as OAroundPosition).elem = fromContext || ev.target as HTMLElement;
			return showFloating(content, anchor, fromContext || ev.target as Node, init);
		} else {
			(anchor as OAroundPosition).elem = ev;
			return showFloating(content, anchor, fromContext || ev as Node, init);
		}
	}

	/** Affiche un menu "libre", . */
	export function showMenu<R = {}>(content: Node, anchor: OPopupAnchor, fromContext: Node, init?: OPopupMenuInit): IPopupable<R> {
		if (init && init.titleBar && typeof init.titleBar !== "string" && !('closeButton' in init.titleBar)) init.titleBar.closeButton = null; //Suppr btn close par défaut.
		const dialog = new PopupMenu().initialize(init);
		dialog.usedOnce = true;
		dialog.appendChild(content);
		dialog.show(anchor, fromContext);
		return dialog;
	}

	/**
	 *
	 * @param fromContext Nécessaire si ev.target est issu d'un menu déconnecté pour retrouver la stack des dialogues courants.
	 */
	export function showMenuFromEvent<R = {}>(content: Node, ev: Event | HTMLElement, fromContext?: HTMLElement, init?: OPopupMenuInit, initSize?: OInitSize): IPopupable<R> {
		const anchor: OPopupAnchor = initSize ? initSize : {};
		if (ev instanceof MouseEvent && ev.pageX > 0 && ev.pageY > 0) {
			(anchor as OFixedPosition).initX = ev.pageX;
			(anchor as OFixedPosition).initY = ev.pageY;
			return showMenu(content, anchor, fromContext || ev.target as Node, init);
		} else if (ev instanceof Event) {
			(anchor as OAroundPosition).elem = fromContext || ev.target as HTMLElement;
			return showMenu(content, anchor, fromContext || ev.target as Node, init);
		} else {
			(anchor as OAroundPosition).elem = ev;
			return showMenu(content, anchor, fromContext || ev as Node, init);
		}
	}

	/**
	 * Associe un tooltip riche à un élément (sans l'afficher).
	 * Le déclenchement au mouseover est géré automatiquement.
	 */
	export function attachTooltip(owner: HTMLElement, content?: Node): PopupTooltip {
		const tooltip = new PopupTooltip().initialize({tooltipOf: owner});
		if (content) tooltip.appendChild(content);
		return tooltip;
	}

	/**
	 * Attache un tooltip à un élément owner en limitant au maximum l'overhead
	 * en retardant sa création à son réel affichage.
	 *
	 * Retourne une fonction qui peut être appelée pour affficher le tooltip prématurément.
	 */
	export function promiseTooltip(owner: HTMLElement, contentBd: (owner: HTMLElement, tooltip: PopupTooltip) => Promise<IView | null> | IView | null, init?: OPopupTooltipInit): () => Promise<undefined | PopupTooltip> {
		return mouseOverRetarderCaller<Promise<undefined | PopupTooltip>>(owner, true, async () => {
			if (!owner.isConnected) return;
			if (!init) init = {tooltipOf: owner};
			else {
				init = Object.create(init);
				init.tooltipOf = owner;
			}
			const tooltip = new PopupTooltip().initialize(init);
			const ct = await contentBd(owner, tooltip);
			if (!ct || !owner.isConnected) return;
			tooltip.appendChild(ct);
			tooltip.show(init.anchor || owner, owner);
			return tooltip;
		});
	}


	/**
	 * Appelle une callback après POPUP.TOOLTIP_retarder ms où la souris est restée au dessus de l'objet overElt.
	 *
	 * Retourne une fonction qui peut etre appelée pour exécuter la callback prématurément.
	 */
	export function mouseOverRetarderCaller<T>(overElt: HTMLElement, doOnce: boolean, cb: () => T): () => T {
		let timer: number;
		const executor = function () {
			if (timer) {
				window.clearTimeout(timer);
				timer = 0;
			}
			if (doOnce) {
				overElt.removeEventListener('pointerenter', lstn);
				overElt.removeEventListener('pointerleave', lstn);
				overElt.removeEventListener('pointercancel', lstn);
			}
			return cb();
		};
		const lstn = function (ev: MouseEvent) {
			if (timer) {
				window.clearTimeout(timer);
				timer = 0;
			}
			if (ev.type === 'pointerenter') timer = window.setTimeout(executor, POPUP.TOOLTIP_retarder);
		};
		overElt.addEventListener('pointerenter', lstn);
		overElt.addEventListener('pointerleave', lstn);
		overElt.addEventListener('pointercancel', lstn);
		return executor;
	}

	export async function closeAllPopup(logicalRoot: Node): Promise<boolean> {
		for (const child of Array.from(document.body.children)) {
			if (IS_Popupable(child) && DOMSH.isLogicalFlatAncestor(logicalRoot, child)) {
				if (!await child.tryClose()) return false;
			}
		}
		return true;
	}

	export let findPopupableParent = findPopupableParentImp;


	/**
	 * Utilisé par le contenu d'un tooltip pour forcer le mode hoverAllowed
	 * sans imposer la touche shift enfoncée.
	 */
	// export function forceTooltipHoverWithoutShift(from: HTMLElement){
	// 	const popup = findPopupableParentImp(from);
	// 	if(popup instanceof PopupTooltip && popup.hoverAllowed) {
	// 		popup.setAttribute("hover-allowed", "");
	// 	}
	// }

	/** Délai avant affichage d'un tooltip. */
	export let TOOLTIP_retarder = 800;
}
